// Centralized structured data schemas for Max Employee
// Use these with SEOHead's structuredData prop or JsonLd.jsx components

const SITE_URL = "https://maxemployee.com";

// ── Organization ──────────────────────────────────────────────────────
export const organizationSchema = {
  "@context": "https://schema.org",
  "@type": "Organization",
  name: "Max Employee",
  url: SITE_URL,
  logo: `${SITE_URL}/og-image-homepage.webp`,
  description: "Deploy autonomous AI digital workers for your business. 24/7 operation, multi-channel, custom skills.",
  sameAs: [
    "https://twitter.com/maxemployee",
    "https://www.linkedin.com/company/maxemployee",
    "https://www.facebook.com/maxemployee",
  ],
  contactPoint: [
    {
      "@type": "ContactPoint",
      contactType: "customer support",
      url: `${SITE_URL}/contact`,
    },
  ],
};

// ── WebSite with SearchAction ─────────────────────────────────────────
export const websiteSchema = {
  "@context": "https://schema.org",
  "@type": "WebSite",
  name: "Max Employee",
  url: SITE_URL,
  description: "AI Digital Workers for Business",
  potentialAction: {
    "@type": "SearchAction",
    target: {
      "@type": "EntryPoint",
      urlTemplate: `${SITE_URL}/blog?q={search_term_string}`,
    },
    "query-input": "required name=search_term_string",
  },
};

// ── SoftwareApplication for Max OS Cloud ──────────────────────────────
export const softwareApplicationSchema = {
  "@context": "https://schema.org",
  "@type": "SoftwareApplication",
  name: "Max OS Cloud",
  url: `${SITE_URL}/products`,
  description: "Autonomous AI digital workers for business. 24/7 operation, multi-channel, custom skills.",
  applicationCategory: "BusinessApplication",
  operatingSystem: "Web",
  offers: {
      "@type": "Offer",
      price: "99",
      priceCurrency: "USD",
      priceValidUntil: "2027-12-31",
      availability: "https://schema.org/InStock",
    },
  aggregateRating: {
    "@type": "AggregateRating",
    ratingValue: "4.8",
    ratingCount: "127",
  },
  featureList: [
    "24/7 autonomous operation",
    "Multi-channel support (WhatsApp, Telegram, Email, Discord, Slack, Instagram DM)",
    "Custom agent skills",
    "Full dashboard with analytics",
    "API access",
    "Priority support",
    "Custom integrations",
    "Dedicated onboarding",
  ],
};

// ── Product (Max OS Cloud) — 3-tier plans ──────────────────────────────
export const productSchema = {
  "@context": "https://schema.org",
  "@type": "Product",
  name: "Max OS Cloud",
  description: "AI digital workers that operate 24/7. From $99/mo.",
  brand: { "@type": "Brand", name: "Max Employee" },
  offers: [
    {
      "@type": "Offer",
      name: "Max OS Cloud Single",
      price: "99",
      priceCurrency: "USD",
      priceSpecification: {
        "@type": "UnitPriceSpecification",
        price: "99",
        priceCurrency: "USD",
        unitText: "MONTH",
      },
      availability: "https://schema.org/InStock",
    },
    {
      "@type": "Offer",
      name: "Multi-Agent",
      price: "249",
      priceCurrency: "USD",
      priceSpecification: {
        "@type": "UnitPriceSpecification",
        price: "249",
        priceCurrency: "USD",
        unitText: "MONTH",
      },
      availability: "https://schema.org/InStock",
    },
    {
      "@type": "Offer",
      name: "Enterprise",
      price: "0",
      priceCurrency: "USD",
      priceSpecification: {
        "@type": "UnitPriceSpecification",
        price: "0",
        priceCurrency: "USD",
        unitText: "MONTH",
      },
      availability: "https://schema.org/InStock",
      description: "Custom pricing — contact sales",
    },
  ],
};

// ── FAQ: Pricing page ─────────────────────────────────────────────────
const pricingFaqs = [
  {
    q: "Is there a free trial?",
    a: "Yes! All plans come with a 7-day free trial. Credit card required. Full refund if cancelled within 7 days.",
  },
  {
    q: "Can I switch plans?",
    a: "Yes, upgrade or downgrade anytime. Changes take effect on your next billing cycle.",
  },
  {
    q: "What happens after the trial?",
    a: "After 7 days, your subscription begins and your card is charged. If you cancel within the trial period, you receive a full refund. You can also cancel anytime after — we'll refund proportionally.",
  },
  {
    q: "What channels are supported?",
    a: "WhatsApp, Telegram, Email, Discord, Slack, Instagram DM, and more.",
  },
  {
    q: "Can I cancel anytime?",
    a: "Yes! Cancel anytime with one click. No questions asked. If you cancel within your 7-day trial, you get a full refund.",
  },
  {
    q: "What's Setup Assist?",
    a: "Our team configures your agent, connects your channels, and trains it on your data. Optional but recommended for first-time users.",
  },
  {
    q: "Do you offer refunds?",
    a: "Yes! Full refund if you cancel within your 7-day trial. After that, we offer proportional refunds based on remaining credit.",
  },
];

export const pricingFaqPageSchema = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: pricingFaqs.map((faq) => ({
    "@type": "Question",
    name: faq.q,
    acceptedAnswer: {
      "@type": "Answer",
      text: faq.a,
    },
  })),
};

// ── FAQ: Landing page ─────────────────────────────────────────────────
const landingFaqs = [
  {
    q: "What exactly is an AI digital worker?",
    a: "An AI digital worker is an autonomous agent that operates 24/7 on your behalf. Unlike chatbots that just respond, Max OS agents take actions — they manage emails, handle support tickets, schedule meetings, and execute tasks across your tools without supervision.",
  },
  {
    q: "What channels does it support?",
    a: "WhatsApp, Telegram, Email, Discord, Slack, Instagram DM, Facebook Messenger, X/Twitter, and more. Your agent receives and responds on all channels from a single dashboard.",
  },
  {
    q: "Can I cancel anytime?",
    a: "Yes! Cancel anytime with one click. No questions asked. If you cancel within your 7-day trial, you get a full refund — no charges, no hassle.",
  },
  {
    q: "Is my data secure?",
    a: "Yes. All data is encrypted at rest and in transit. We use enterprise-grade security with SOC 2 compliance. Your agent's knowledge base is isolated to your account only.",
  },
  {
    q: "What happens if the AI makes a mistake?",
    a: "Max OS includes smart escalation — when the agent is unsure, it asks for human approval before acting. You can also set confidence thresholds and review all agent actions in the dashboard.",
  },
  {
    q: "Can I use Max OS with my existing tools?",
    a: "Absolutely. Max OS integrates with 17+ platforms including WhatsApp, Telegram, Email, Slack, Discord, Google Workspace, and any service with a webhook or API.",
  },
  {
    q: "Do I need technical skills to set up?",
    a: "No. Setup takes 5 minutes — choose your agent, connect your channels, and go live. No coding, no technical knowledge required. Our Setup Assist option handles everything for you.",
  },
  {
    q: "What's the difference between Max OS Cloud and Enterprise?",
    a: "Max OS Cloud gives you 1 AI agent on a shared infrastructure. Enterprise gives you multiple agents with custom workflows, dedicated VM, SLA guarantees, and dedicated onboarding.",
  },
  {
    q: "Can I try before I buy?",
    a: "Yes! All plans include a 7-day free trial. Credit card required to start, but you can cancel within 7 days for a full refund. No questions asked.",
  },
  {
    q: "How is this different from ChatGPT?",
    a: "ChatGPT is a chatbot — it only responds when you ask. Max OS is an autonomous AI worker that proactively handles tasks 24/7, connects to your channels, and takes actions on your behalf. It's the difference between a tool and an employee.",
  },
];

export const landingFaqPageSchema = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: landingFaqs.map((faq) => ({
    "@type": "Question",
    name: faq.q,
    acceptedAnswer: {
      "@type": "Answer",
      text: faq.a,
    },
  })),
};

// ── Breadcrumb: Pricing ───────────────────────────────────────────────
export const pricingBreadcrumbSchema = {
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  itemListElement: [
    { "@type": "ListItem", position: 1, name: "Home", item: `${SITE_URL}/` },
    { "@type": "ListItem", position: 2, name: "Pricing", item: `${SITE_URL}/pricing` },
  ],
};

// ── Breadcrumb: Products ──────────────────────────────────────────────
export const productsBreadcrumbSchema = {
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  itemListElement: [
    { "@type": "ListItem", position: 1, name: "Home", item: `${SITE_URL}/` },
    { "@type": "ListItem", position: 2, name: "Products", item: `${SITE_URL}/products` },
  ],
};

// ── Breadcrumb: Home ──────────────────────────────────────────────────
export const homeBreadcrumbSchema = {
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  itemListElement: [
    { "@type": "ListItem", position: 1, name: "Home", item: `${SITE_URL}/` },
  ],
};
